// @ts-check
import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import cogoToast from 'cogo-toast';
import { useHistory, useLocation } from 'react-router-dom';
import { getConciergePromptConfigs, createConciergePromptConfig, updateConciergePromptConfig } from '../../api/concierge_prompt_configs';
import Loader from '../General/Loader';

const TYPES = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'gradekeeper', label: 'Gradekeeper' },
  { value: 'notification_claimed', label: 'Notif: Claimed' },
  { value: 'notification_unclaimed', label: 'Notif: Unclaimed' },
  { value: 'notification_submitted', label: 'Notif: Submitted' },
  { value: 'notification_curator_relay', label: 'Notif: Curator Relay' },
  { value: 'notification_tier_upgrade', label: 'Notif: Tier Upgrade' },
  { value: 'notification_promo_redeem', label: 'Notif: Promo Redeem' },
  { value: 'image_description', label: 'System: Image Description' }
];

const EMPTY_CONFIG = {
  type: 'onboarding',
  name: '',
  systemPrompt: '',
  prompt: '',
  model: 'anthropic/claude-opus-4.6',
  notes: ''
};

const PROMPT_VARIABLES = [
  { value: '<shopperLLMBio>', label: 'Shopper LLM Bio' },
  { value: '<shopperName>', label: 'Shopper Name' },
  { value: '<shopperEmail>', label: 'Shopper Email' },
  { value: '<shopperPhone>', label: 'Shopper Phone' },
  { value: '<shopperTier>', label: 'Shopper Tier' },
  { value: '<currentTimeET>', label: 'Current Time (ET)' },
  { value: '<noirPrompt>', label: 'Noir Prompt (Dispatcher)' }
];

const VariableBar = ({ textAreaRef, onSetValue }) => {
  const onAddVariable = variable => {
    const el = textAreaRef.current;
    if (!el) return;

    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    const value = el.value;

    const next = el.value.slice(0, start) + variable + value.slice(end);
    onSetValue(next);

    requestAnimationFrame(() => {
      el.focus();
      const pos = start + variable.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className='variable-row'>
      <span>Variables:</span>
      {PROMPT_VARIABLES.map(v => (
        <button key={v.value} value={v.value} onClick={() => onAddVariable(v.value)}>
          {v.label}
        </button>
      ))}
    </div>
  );
};

const ConfigEditor = ({ editingConfig, setEditingConfig, onSave, onCancel, isCreating }) => {
  const systemPromptRef = useRef(null);
  const promptRef = useRef(null);

  return (
    <div className='config-editor'>
      <div className='editor-row'>
        <label>Name</label>
        <input
          type='text'
          value={editingConfig.name}
          onChange={e => setEditingConfig({ ...editingConfig, name: e.target.value })}
          placeholder='e.g. Onboarding v1'
        />
      </div>
      <div className='editor-row'>
        <label>Model</label>
        <input
          type='text'
          value={editingConfig.model}
          onChange={e => setEditingConfig({ ...editingConfig, model: e.target.value })}
          placeholder='openai/gpt-4o'
        />
      </div>
      <div className='editor-row'>
        <div className='editor-row-header'>
          <label>System Prompt</label>
          <span className='editor-row-hint'>Tool training, behavioral rules, and system instructions</span>
        </div>
        <textarea
          value={editingConfig.systemPrompt || ''}
          onChange={e => setEditingConfig({ ...editingConfig, systemPrompt: e.target.value })}
          placeholder='Enter tool training and system rules...'
          ref={systemPromptRef}
          rows={10}
        />
        <VariableBar textAreaRef={systemPromptRef} onSetValue={value => setEditingConfig({ ...editingConfig, systemPrompt: value })} />
      </div>
      <div className='editor-row'>
        <div className='editor-row-header'>
          <label>Prompt</label>
          <span className='editor-row-hint'>Conversational instructions and persona</span>
        </div>
        <textarea
          value={editingConfig.prompt}
          onChange={e => setEditingConfig({ ...editingConfig, prompt: e.target.value })}
          placeholder='Enter the conversational prompt for this dispatcher...'
          ref={promptRef}
          rows={10}
        />
        <VariableBar textAreaRef={promptRef} onSetValue={value => setEditingConfig({ ...editingConfig, prompt: value })} />
      </div>
      <div className='editor-row'>
        <label>Notes</label>
        <textarea
          value={editingConfig.notes || ''}
          onChange={e => setEditingConfig({ ...editingConfig, notes: e.target.value })}
          placeholder='Optional notes about this config...'
          rows={3}
        />
      </div>
      <div className='editor-actions'>
        <button className='button-cancel' onClick={onCancel}>
          Cancel
        </button>
        <button className='button-save' onClick={onSave}>
          {isCreating ? 'Create' : 'Save as New Version'}
        </button>
      </div>
    </div>
  );
};

const NoirDispatchers = () => {
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const filterType = params.get('filter') || TYPES[0].value;

  const setFilterType = filter => {
    const next = new URLSearchParams(location.search);
    next.set('filter', filter);
    history.replace({ search: next.toString() });
  };

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const data = await getConciergePromptConfigs(filterType);
      setConfigs(data.configs || []);
    } catch {
      cogoToast.error('Failed to load dispatcher configs.');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    setLoading(true);
    fetchConfigs();
  }, [fetchConfigs]);

  const handleCreate = async () => {
    if (!editingConfig?.name || !editingConfig?.prompt) {
      return cogoToast.warn('Name and prompt are required.');
    }
    try {
      await createConciergePromptConfig(editingConfig);
      cogoToast.success('Dispatcher created.');
      setEditingConfig(null);
      setIsCreating(false);
      fetchConfigs();
    } catch {
      cogoToast.error('Failed to create dispatcher.');
    }
  };

  const handleUpdate = async () => {
    if (!editingConfig) return;
    if (!editingConfig.name || !editingConfig.prompt) {
      return cogoToast.warn('Name and prompt are required.');
    }
    try {
      await createConciergePromptConfig({
        type: editingConfig.type,
        name: editingConfig.name,
        systemPrompt: editingConfig.systemPrompt,
        prompt: editingConfig.prompt,
        model: editingConfig.model,
        notes: editingConfig.notes,
        parentConciergePromptConfig_id: editingConfig.id
      });
      cogoToast.success('New version created.');
      setEditingConfig(null);
      fetchConfigs();
    } catch {
      cogoToast.error('Failed to create new version.');
    }
  };

  const handleActivate = async config => {
    try {
      await updateConciergePromptConfig(config.id, { isActive: true });
      cogoToast.success(`"${config.name}" is now active.`);
      fetchConfigs();
    } catch {
      cogoToast.error('Failed to activate.');
    }
  };

  const startCreate = () => {
    setEditingConfig({ ...EMPTY_CONFIG, type: filterType });
    setIsCreating(true);
  };

  const startEdit = config => {
    setEditingConfig({ ...config });
    setIsCreating(false);
  };

  const cancelEdit = () => {
    setEditingConfig(null);
    setIsCreating(false);
  };

  if (loading) return <Loader />;

  const isEditingExisting = editingConfig && !isCreating;

  return (
    <div className='noir-dispatchers'>
      <div className='dispatchers-toolbar'>
        <div className='filter-tabs'>
          {TYPES.map(t => (
            <div key={t.value} className={cn('filter-tab', { active: filterType === t.value })} onClick={() => setFilterType(t.value)}>
              {t.label}
            </div>
          ))}
        </div>
        <button className='button-add' onClick={startCreate}>
          + New Version
        </button>
      </div>

      {isCreating && editingConfig && (
        <ConfigEditor editingConfig={editingConfig} setEditingConfig={setEditingConfig} onSave={handleCreate} onCancel={cancelEdit} isCreating />
      )}

      <div className='configs-list'>
        {configs.length === 0 ? (
          <div className='empty-state'>No dispatchers configured yet. Create one to get started.</div>
        ) : (
          [...configs]
            .sort((a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0))
            .map((config, i, sorted) => {
              const isLastActive = config.isActive && !sorted[i + 1]?.isActive;
              const isEditing = isEditingExisting && editingConfig.id === config.id;

              if (isEditing) {
                return (
                  <div key={config.id} className={cn('config-card', { active: config.isActive, editing: true, 'active-divider': isLastActive })}>
                    <ConfigEditor
                      editingConfig={editingConfig}
                      setEditingConfig={setEditingConfig}
                      onSave={handleUpdate}
                      onCancel={cancelEdit}
                      isCreating={false}
                    />
                  </div>
                );
              }

              return (
                <div key={config.id} className={cn('config-card', { active: config.isActive, 'active-divider': isLastActive })}>
                  <div className='config-card-header'>
                    <div className='config-info'>
                      <span className='config-name'>{config.name}</span>
                      <span className={cn('config-type-badge', config.type)}>{config.type}</span>
                      {!!config.isActive && <span className='active-badge'>ACTIVE</span>}
                    </div>
                    <div className='config-meta'>
                      <span className='config-model'>{config.model}</span>
                    </div>
                  </div>
                  <div className='config-card-body'>
                    {config.systemPrompt && (
                      <p className='config-prompt-preview'>
                        <strong>System:</strong>{' '}
                        {config.systemPrompt.length > 100 ? `${config.systemPrompt.substring(0, 100)}...` : config.systemPrompt}
                      </p>
                    )}
                    <p className='config-prompt-preview'>
                      <strong>Prompt:</strong> {config.prompt.length > 200 ? `${config.prompt.substring(0, 200)}...` : config.prompt}
                    </p>
                    {config.notes && <p className='config-notes'>Note: {config.notes}</p>}
                  </div>
                  <div className='config-card-actions'>
                    <button
                      className='button-conversations'
                      onClick={() => history.replace({ search: `tab=conversations&promptConfigId=${config.id}` })}
                    >
                      Conversations
                    </button>
                    <button className='button-edit' onClick={() => startEdit(config)}>
                      New Version
                    </button>
                    {!config.isActive && (
                      <button className='button-activate' onClick={() => handleActivate(config)}>
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
};

export default NoirDispatchers;
